import './hideout.css';
import { JUKEBOX_STATIONS, type HideoutItemId, type JukeboxStation } from '../../shared/hideout';
import type { HideoutView } from '../../shared/messages/hideout';
import { el, esc, setText, toggle } from '../ui/dom';
import type { ModuleOverlay } from '../ui/uiTypes';
import { itemCards, pounds, whoIsIn } from './hideoutFormat';

/**
 * The DOM over the hideout (WP-X4, docs/HIDEOUT.md, docs/UI.md): the room is the picture (3D behind this layer,
 * framed into the **stage**); over it a slim head (where you are, the treasury, "✕ Back to the street") and the
 * **panel**: who's in, the furnishings (each: what it is, bought by whom — or the price and BUY, or plainly why
 * not), the jukebox's dial once the gang has one, and a line saying what just happened. It draws what the hideout
 * system hands it and reports taps and keys back; it never buys.
 *
 * It goes up through the UI's module-overlay contract (as the book and Sid's back room): the touch layer steps
 * aside, menu keys and the pad come in through `onNav` (↑ ↓ pick a row, Enter buys it, Esc goes out).
 */

export interface HideoutPanelState {
  touch: boolean;
  view: HideoutView | null;
  /** The station the jukebox plays here (the server's), null: off. */
  station: JukeboxStation | null;
  /** Station names by id (the wireless's own names). */
  stationNames: Record<string, string>;
  /** The row with the keyboard's focus (-1: none). */
  focus: number;
  note: string;
  noteBad: boolean;
  busy: boolean;
}

export interface HideoutPanelActions {
  buy(item: HideoutItemId): void;
  station(s: JukeboxStation | null): void;
  focus(i: number): void;
  leave(): void;
}

export class HideoutPanel implements ModuleOverlay {
  readonly root: HTMLElement;
  readonly stage: HTMLElement;
  private readonly fade: HTMLElement;
  private readonly room: HTMLElement;
  private readonly title: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly side: HTMLElement;
  private readonly stamp: HTMLElement;
  private readonly foot: HTMLElement;
  private key = '';
  private state: HideoutPanelState | null = null;
  private stampTimer = 0;

  constructor(
    parent: HTMLElement,
    private readonly act: HideoutPanelActions,
  ) {
    this.root = el('div', 'bh-ui hd-layer', undefined, parent);
    this.root.hidden = true;
    this.fade = el('div', 'hd-fade', undefined, this.root);
    this.room = el('div', 'hd-room', undefined, this.root);
    this.room.hidden = true;
    this.room.setAttribute('role', 'dialog');
    this.room.setAttribute('aria-label', 'The hideout');

    const head = el('div', 'hd-head', undefined, this.room);
    const t = el('div', 'hd-title', undefined, head);
    el('div', 'hd-kicker', '9 Pilcher Street · The hideout', t);
    this.title = el('h2', '', 'The hideout', t);
    const purse = el('div', 'hd-purse', undefined, head);
    el('small', '', 'Treasury', purse);
    this.purse = el('b', '', '', purse);
    const x = el('button', 'hd-x', undefined, head);
    x.type = 'button';
    x.setAttribute('aria-label', 'Back to the street');
    x.title = 'Back to the street (Esc)';
    el('span', 'x', '✕', x);
    el('span', 'l', 'Back to the street', x);
    x.addEventListener('click', (e) => {
      e.stopPropagation();
      this.act.leave();
    });

    this.stage = el('div', 'hd-stage', undefined, this.room);
    this.stamp = el('div', 'hd-stamp', '', this.stage);
    this.stamp.hidden = true;

    this.side = el('aside', 'hd-side', undefined, this.room);
    this.side.dataset.touchScroll = '';
    this.side.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('button');
      if (!b || b.disabled) return;
      e.stopPropagation();
      if (e.detail > 0) b.blur();
      if (b.dataset.buy) this.act.buy(b.dataset.buy as HideoutItemId);
      else if (b.dataset.station !== undefined) this.act.station(b.dataset.station ? (b.dataset.station as JukeboxStation) : null);
    });
    this.foot = el('div', 'hd-foot', '', this.room);
  }

  setFade(v: number): void {
    this.fade.style.opacity = v.toFixed(3);
    this.fade.hidden = v <= 0.001;
  }

  setOpen(on: boolean): void {
    this.root.hidden = !on;
    if (!on) {
      this.room.hidden = true;
      this.key = '';
      this.stamp.hidden = true;
    }
  }

  setRoom(on: boolean): void {
    this.room.hidden = !on;
  }

  get roomShown(): boolean {
    return !this.root.hidden && !this.room.hidden;
  }

  /** A purchase went through (or was refused: `bad`): a stamp lands on the stage. */
  celebrate(text: string, bad = false): void {
    this.stamp.textContent = text;
    this.stamp.hidden = false;
    toggle(this.stamp, 'bad', bad);
    this.stamp.classList.remove('go');
    void this.stamp.offsetWidth;
    this.stamp.classList.add('go');
    clearTimeout(this.stampTimer);
    this.stampTimer = window.setTimeout(() => (this.stamp.hidden = true), bad ? 1200 : 1700);
  }

  /** Where a furnishing's BUY button is on screen (CSS px), for a real tap in tests; null if it isn't there. */
  buttonCenter(item: string): { x: number; y: number } | null {
    const b = this.side.querySelector<HTMLElement>(`button[data-buy="${item}"]`);
    if (!b || !this.roomShown) return null;
    const r = b.getBoundingClientRect();
    return r.width > 0 ? { x: Math.round(r.left + r.width / 2), y: Math.round(r.top + r.height / 2) } : null;
  }

  render(s: HideoutPanelState): void {
    this.state = s;
    const v = s.view;
    setText(this.title, v ? v.name : 'The hideout');
    setText(this.purse, v ? pounds(v.treasury) : '—');
    toggle(this.room, 'touch', s.touch);
    toggle(this.room, 'busy', s.busy);
    setText(this.foot, s.touch ? '' : '↑ ↓ choose · Enter buy · Esc back to the street');
    const key = JSON.stringify([v, s.station, s.focus, s.note, s.noteBad, s.busy, s.touch]);
    if (key === this.key) return;
    this.key = key;
    if (!v) {
      this.side.innerHTML = `<p class="hd-wait">Opening up…</p>`;
      return;
    }
    const cards = itemCards(v);
    const rows = cards
      .map((c, i) => {
        const btn = c.action
          ? `<button type="button" class="hd-buy" data-buy="${c.def.id}"${c.action.enabled && !s.busy ? '' : ' disabled'}>${esc(s.busy && i === s.focus ? 'Paying…' : c.action.label)}</button>`
          : `<span class="hd-got">✓ In</span>`;
        return (
          `<li class="hd-item${c.owned ? ' owned' : ''}${i === s.focus ? ' focus' : ''}" data-i="${i}">` +
          `<div class="hd-it"><b>${esc(c.def.name)}</b><small>${esc(c.owned ? c.sub : c.def.blurb)}</small>${!c.owned && c.why ? `<em>${esc(c.why)}</em>` : ''}</div>${btn}</li>`
        );
      })
      .join('');
    const juke = v.items.some((i) => i.id === 'jukebox');
    const dial = juke
      ? `<section class="hd-juke"><h4>The jukebox</h4><div class="hd-dial">` +
        [null, ...JUKEBOX_STATIONS]
          .map((id) => `<button type="button" data-station="${id ?? ''}" class="${(id ?? null) === s.station ? 'on' : ''}"${s.busy ? ' disabled' : ''}>${esc(id ? (s.stationNames[id] ?? id) : 'Off')}</button>`)
          .join('') +
        `</div></section>`
      : '';
    const got = v.items.length;
    this.side.innerHTML =
      `<p class="hd-who">${esc(whoIsIn(v))} <span>A safe spot: nobody can hurt you round here.</span></p>` +
      dial +
      `<h4>Furnishings <small>${got} of ${cards.length}</small></h4>` +
      `<ul class="hd-items">${rows}</ul>` +
      `<p class="hd-note${s.noteBad ? ' bad' : ''}" aria-live="polite">${esc(s.note || (v.you.furnish ? 'Bought from the treasury, for good. Looks only — nothing here makes anyone tougher or faster.' : 'Your role can look but not buy: a boss or a lieutenant furnishes the place.'))}</p>`;
  }

  onNav(a: 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back'): boolean {
    if (this.root.hidden) return false;
    const s = this.state;
    const n = s?.view ? itemCards(s.view).length : 0;
    if (a === 'back') this.act.leave();
    else if (a === 'up' || a === 'left') this.act.focus(Math.max(0, (s?.focus ?? 0) - 1));
    else if (a === 'down' || a === 'right') this.act.focus(Math.min(n - 1, (s?.focus ?? -1) + 1));
    else if (a === 'confirm' && s?.view) {
      const c = itemCards(s.view)[s.focus];
      if (c?.action?.enabled) this.act.buy(c.def.id);
    }
    return true;
  }

  dispose(): void {
    clearTimeout(this.stampTimer);
    this.root.remove();
  }
}
